import React, { useEffect, useState, useCallback } from "react";
import { EyeIcon, HeartIcon, PencilIcon, XMarkIcon } from "@heroicons/react/24/outline";
import AddPartnership from "./AddPartnership";
import EditPartnershipInformation from "./EditPartnershipInformation";
import Table from "../../common/Table";
import { institutionAPI } from "../../../services/api";
import PartnershipView from "./PartnershipView";

export default function ManagePartnership({ isOpen, onClose }) {
  const [selectedPartnership, setSelectedPartnership] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingPartnership, setEditingPartnership] = useState(null);
  const [partnerships, setPartnerships] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    perPage: 10,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch partnerships from backend
  const fetchPartnerships = useCallback(
    async (search = "", page = 1, perPage = pagination.perPage) => {
      try {
        setLoading(true);
        const response = await institutionAPI.getPartnerships({
          page,
          per_page: perPage,
          search,
        });

        const data = response?.data?.data?.partnerships ?? [];
        const meta = response?.data?.data?.meta ?? {};

        setPartnerships(data);
        setPagination({
          currentPage: Number(meta?.current_page) || page,
          lastPage: Number(meta?.last_page) || 1,
          perPage: Number(meta?.per_page) || perPage,
          total: Number(meta?.total) || data.length,
        });
      } catch (error) {
        console.error("Error fetching partnerships:", error);
        setPartnerships([]);
      } finally {
        setLoading(false);
      }
    },
    [pagination.perPage]
  );

  useEffect(() => {
    if (isOpen) fetchPartnerships();
  }, [isOpen, fetchPartnerships]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchPartnerships(searchTerm);
    }, 100);

    return () => clearTimeout(debounce);
  }, [searchTerm, fetchPartnerships]);

  const handleSave = (newPartnership) => {
    setPartnerships((prev) => {
      const exists = prev.find((p) => p.partnership_id === newPartnership.partnership_id);
      const updated = exists
        ? prev.map((p) =>
            p.partnership_id === newPartnership.partnership_id ? newPartnership : p
          )
        : [newPartnership, ...prev];
      return updated;
    });
  };

  if (!isOpen) return null;

  const columns = [
    {
      key: "partner_name",
      label: "PARTNER NAME",
      render: (_, p) => <span className="text-sm">{p.partner_name || "N/A"}</span>,
    },
    {
      key: "partner_type",
      label: "PARTNERSHIP TYPE",
      render: (_, p) => (
        <span className="text-sm">
          {typeof p.partner_type === "object"
            ? p.partner_type?.name || "N/A"
            : p.partner_type || "N/A"}
        </span>
      ),
    },
    {
      key: "agreement_title",
      label: "TYPE OF COLLABORATION",
      render: (_, p) => (
        <span className="text-sm">
          {typeof p.agreement_title === "object"
            ? p.agreement_title?.name || "N/A"
            : p.agreement_title || "N/A"}
        </span>
      ),
    },
    {
      key: "agreement_date",
      label: "DATE INITIATED",
      render: (_, p) => (
        <span className="text-sm">{p.agreement_date?.split("T")[0] || "N/A"}</span>
      ),
    },
    {
      key: "status",
      label: "STATUS",
      render: (_, p) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            p.status?.toLowerCase() === "active"
              ? "bg-green-100 text-green-700"
              : "bg-gray-200 text-gray-600"
          }`}
        >
          {p.status || "N/A"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "ACTIONS",
      render: (_, p) => (
        <div className="flex gap-2">
          <button
            className="text-blue-600 hover:text-blue-900"
            onClick={() => {
              setSelectedPartnership(p);
              setEditingPartnership(p);
              setViewOpen(true);
            }}
          >
            <EyeIcon className="w-4 h-4" />
          </button>
          <button
            className="text-yellow-600 hover:text-yellow-900"
            onClick={() => {
              setEditingPartnership(p);
              setEditOpen(true);
            }}
          >
            <PencilIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-center overflow-y-auto py-10">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />

      {/* Modal Content */}
      {/* <div className="relative w-full max-w-5xl bg-white rounded-lg shadow-lg p-4 my-auto"> */}
      <div className="relative w-full max-w-5xl bg-white rounded-lg shadow-lg my-auto flex flex-col max-h-[80vh]">
        {/* Header */}
        {/* <div className="flex justify-between items-center border-b p-4"> */}
         <div className="flex justify-between items-center border-b p-4 flex-shrink-0">
          <h2 className="text-lg font-semibold">Manage Partnerships</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
        {/* Title + Add + Search */}
        {/* <div className="flex justify-between items-center p-4"> */}
         <div className="flex justify-between items-center p-4">
          <div className="flex items-center gap-2"> 
            <span className="bg-blue-200/50 rounded-full text-2xl">
              <HeartIcon className="w-4 h-4 text-blue-500 m-2" />
            </span>
            <p className="font-medium">Community Partnership Coordination</p>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Search partnerships..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700"
              onClick={() => setAddOpen(true)}
            >
              + New Partnership
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="p-4">
          {loading ? (
            <p className="text-center py-6 text-gray-500">Loading partnerships...</p>
          ) : partnerships.length === 0 ? (
            <p className="text-center py-6 text-gray-500">No partnerships found.</p>
          ) : (
            <Table
              noContainer={true}
              columns={columns}
              data={partnerships}
              getRowKey={(p, idx) => p.partnership_id || idx}
              pagination={{
                currentPage: pagination.currentPage,
                lastPage: pagination.lastPage,
                perPage: pagination.perPage,
                total: pagination.total,
              }}
              onPageChange={(page) =>
                fetchPartnerships(searchTerm, page, pagination.perPage)
              }
              paginationProps={{
                showSizeSelector: true,
                isLoading: loading,
                onPerPageChange: (newPerPage) =>
                  fetchPartnerships(searchTerm, 1, newPerPage),
              }}
            />
          )}
        </div>
      </div>
      </div>

      {/* View Modal */}
      <PartnershipView
        isOpen={viewOpen}
        onClose={() => setViewOpen(false)}
        partnership={selectedPartnership}
        onEdit={() => {
          setViewOpen(false);
          setEditOpen(true);
        }}
      />

      {/* Add Modal */}
      <AddPartnership isOpen={addOpen} onClose={() => setAddOpen(false)} onSave={handleSave} />

      {/* Edit Modal */}
      <EditPartnershipInformation
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        partnership={editingPartnership}
        onSave={handleSave}
      />
    </div>
  );
}
